/**
 * Ola · Pedido por WhatsApp → Meta.
 *
 * Al tocar «Enviar pedido por WhatsApp» (snippets/ola-pedido-whatsapp.liquid) se cuenta el pedido
 * en el píxel de Meta como un contacto (`Contact`), con el total y los productos del carrito. Es un
 * resultado aparte de las compras: en el Administrador de anuncios se ve en la columna «Contactos».
 * El enlace sigue abriendo WhatsApp igual que antes.
 *
 * El píxel de la tienda lo carga la app de Meta de Shopify en un espacio aislado, así que aquí se
 * carga la librería de Meta aparte, solo para este evento: sin PageView y sin seguimiento automático
 * de clics. Usa la cookie `_fbp` de la tienda, así que es la misma persona para Meta.
 *
 * Datos que lee del enlace: `data-pixel`, `data-valor`, `data-moneda`, `data-variantes` (IDs de
 * variante separados por comas, que son los IDs de los productos del catálogo) y `data-unidades`.
 */

const SELECTOR = '[data-ola-wa-pedido]';
const LIBRERIA = 'https://connect.facebook.net/en_US/fbevents.js';
const NOMBRE_EVENTO = 'Pedido por WhatsApp';
// El mismo carrito tocado dos veces seguidas cuenta un solo contacto.
const ESPERA_REPETIDO_MS = 10 * 60 * 1000;

/** @type {Set<string>} */
const pixelesIniciados = new Set();
let ultimoPedido = { firma: '', hora: 0 };

/** Respeta el aviso de privacidad de Shopify si la tienda lo activa en algún mercado. */
const marketingPermitido = () => {
  const privacidad = /** @type {any} */ (window).Shopify?.customerPrivacy;
  if (privacidad && typeof privacidad.marketingAllowed === 'function') return privacidad.marketingAllowed();
  return true;
};

/**
 * Deja listo `fbq` (el código base de Meta: guarda las llamadas hasta que carga la librería).
 * @param {string} pixel
 */
const prepararPixel = (pixel) => {
  const w = /** @type {any} */ (window);
  if (!w.fbq) {
    const fbq = function () {
      // eslint-disable-next-line prefer-rest-params
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = /** @type {IArguments[]} */ ([]);
    /** @type {((...args: unknown[]) => void) | undefined} */
    fbq.callMethod = undefined;
    w.fbq = fbq;
    if (!w._fbq) w._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = LIBRERIA;
    document.head.append(script);
  }

  if (!pixelesIniciados.has(pixel)) {
    w.fbq('set', 'autoConfig', false, pixel);
    w.fbq('init', pixel);
    pixelesIniciados.add(pixel);
  }
  return w.fbq;
};

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
const enlaceDelPedido = (target) =>
  target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest(SELECTOR)) : null;

/** Empieza a cargar la librería en cuanto la persona va a tocar el botón. */
const alAcercarse = (/** @type {Event} */ event) => {
  const enlace = enlaceDelPedido(event.target);
  const pixel = enlace?.dataset.pixel?.trim();
  if (!enlace || !pixel || !marketingPermitido()) return;
  prepararPixel(pixel);
};

const alTocar = (/** @type {MouseEvent} */ event) => {
  const enlace = enlaceDelPedido(event.target);
  const pixel = enlace?.dataset.pixel?.trim();
  if (!enlace || !pixel || !marketingPermitido()) return;

  const variantes = (enlace.dataset.variantes || '').split(',').filter(Boolean);
  const valor = Number(enlace.dataset.valor) || 0;
  const firma = `${variantes.join(',')}|${valor}`;
  const ahora = Date.now();
  if (firma === ultimoPedido.firma && ahora - ultimoPedido.hora < ESPERA_REPETIDO_MS) return;
  ultimoPedido = { firma, hora: ahora };

  const fbq = prepararPixel(pixel);
  fbq(
    'trackSingle',
    pixel,
    'Contact',
    {
      value: valor,
      currency: enlace.dataset.moneda || 'HNL',
      content_ids: variantes,
      content_type: 'product',
      num_items: Number(enlace.dataset.unidades) || variantes.length,
      content_name: NOMBRE_EVENTO,
    },
    { eventID: `ola-wa-${ahora}` }
  );
};

document.addEventListener('pointerdown', alAcercarse, { passive: true });
document.addEventListener('focusin', alAcercarse);
document.addEventListener('click', alTocar);
